/** Admin Roles & Permissions — list helpers + fixtures for offline/detail fallbacks. */

export const ROLES_VIEW_TABS = [{ key: "roles", label: "Roles" }]

const formatCount = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

export const mapRolesSummaryKpi = (result = {}) => [
  {
    key: "total",
    title: "Total Roles",
    value: formatCount(result.total_roles),
    tone: "lightblue-custom",
  },
  {
    key: "active",
    title: "Active Roles",
    value: formatCount(result.active_roles),
    tone: "lightteal",
  },
  {
    key: "permissions",
    title: "Permissions",
    value: formatCount(result.permissions),
    tone: "peach",
  },
]

/**
 * Map GET /api/v1/admin/roles/get-all row → table row.
 */
export const mapAdminRoleRow = item => {
  const permissions = Array.isArray(item.permissions) ? item.permissions : []
  const cities = Array.isArray(item.cities) ? item.cities : []
  const usersRaw = Number(
    item.users_count ??
      item.users_assigned ??
      item.stats?.users_assigned ??
      (Array.isArray(item.users) ? item.users.length : NaN),
  )
  const isActive =
    typeof item.is_active === "boolean"
      ? item.is_active
      : item.status !== "inactive"

  return {
    id: item.id,
    name: item.name || "--",
    description: item.description || "--",
    users: Number.isFinite(usersRaw) ? usersRaw : 0,
    permissionsCount: permissions.length,
    citiesCount: cities.length,
    type: item.created_by ? "custom" : "system",
    status: isActive ? "active" : "inactive",
    lastUpdated: item.updated_at || item.created_at || "",
    permissionIds: permissions.map(p => p.id).filter(Boolean),
    raw: item,
  }
}

/** Static Figma fixtures (detail fallback / local demos). */
export const ROLE_ROWS = [
  {
    id: "1",
    name: "Super Admin",
    description: "Full system access with all permissions",
    users: 8,
    type: "system",
    status: "active",
    lastUpdated: "Jun 15, 2025",
  },
  {
    id: "2",
    name: "City Admin",
    description: "Manage city, review, comments and user activities",
    users: 6,
    type: "system",
    status: "active",
    lastUpdated: "Apr 30, 2025",
  },
  {
    id: "3",
    name: "Analyst",
    description: "Analyzes user data and generates reports",
    users: 7,
    type: "system",
    status: "active",
    lastUpdated: "Jan 17, 2025",
  },
  {
    id: "4",
    name: "Support Agent",
    description: "Handles customer queries and provides technical assistance",
    users: 12,
    type: "system",
    status: "active",
    lastUpdated: "Feb 20, 2025",
  },
  {
    id: "5",
    name: "QA Tester",
    description: "Tests features to ensure bug-free releases",
    users: 18,
    type: "custom",
    status: "active",
    lastUpdated: "Apr 22, 2025",
  },
  {
    id: "6",
    name: "Marketing Lead",
    description: "Plans campaigns and manages marketing strategies",
    users: 9,
    type: "custom",
    status: "active",
    lastUpdated: "May 15, 2025",
  },
  {
    id: "7",
    name: "Moderator",
    description: "Manages user comments and flags inappropriate content",
    users: 30,
    type: "system",
    status: "active",
    lastUpdated: "May 05, 2025",
  },
  {
    id: "8",
    name: "Content Editor",
    description: "Can create and edit content but cannot publish",
    users: 24,
    type: "custom",
    status: "active",
    lastUpdated: "Apr 10, 2025",
  },
  {
    id: "9",
    name: "Content Writer",
    description: "Can create and edit content but cannot publish",
    users: 24,
    type: "custom",
    status: "active",
    lastUpdated: "Apr 10, 2025",
  },
  {
    id: "10",
    name: "Finance Manager",
    description: "Oversees budgeting and financial reporting",
    users: 4,
    type: "system",
    status: "active",
    lastUpdated: "Feb 28, 2025",
  },
  {
    id: "11",
    name: "HR Manager",
    description: "Manages recruitment and employee relations",
    users: 5,
    type: "custom",
    status: "active",
    lastUpdated: "Mar 12, 2025",
  },
  {
    id: "12",
    name: "Developer",
    description: "Develops and maintains software features",
    users: 10,
    type: "custom",
    status: "active",
    lastUpdated: "Jun 01, 2025",
  },
  {
    id: "13",
    name: "Operations Admin",
    description: "Full system access with all permissions",
    users: 8,
    type: "custom",
    status: "active",
    lastUpdated: "Jun 15, 2025",
  },
  {
    id: "14",
    name: "Publisher",
    description: "Responsible for reviewing and publishing content",
    users: 15,
    type: "custom",
    status: "active",
    lastUpdated: "Mar 28, 2025",
  },
  {
    id: "15",
    name: "Project Manager",
    description: "Coordinates project timelines and deliverables",
    users: 11,
    type: "system",
    status: "active",
    lastUpdated: "Jun 10, 2025",
  },
  {
    id: "16",
    name: "Member",
    description: "Standard member access to community features",
    users: 4210,
    type: "system",
    status: "active",
    lastUpdated: "Jun 18, 2025",
  },
  {
    id: "17",
    name: "City Lead",
    description: "Leads city programming and local member engagement",
    users: 145,
    type: "custom",
    status: "active",
    lastUpdated: "May 30, 2025",
  },
  {
    id: "18",
    name: "Photography Reviewer",
    description: "Reviews and approves photography submissions",
    users: 22,
    type: "custom",
    status: "inactive",
    lastUpdated: "Apr 05, 2025",
  },
  {
    id: "19",
    name: "Events Coordinator",
    description: "Creates and manages city events and sessions",
    users: 34,
    type: "custom",
    status: "active",
    lastUpdated: "Jun 02, 2025",
  },
  {
    id: "20",
    name: "Rewards Manager",
    description: "Manages XP rewards and redemption workflows",
    users: 6,
    type: "system",
    status: "active",
    lastUpdated: "May 22, 2025",
  },
  {
    id: "21",
    name: "Read Only",
    description: "View-only access across admin dashboards",
    users: 3,
    type: "system",
    status: "inactive",
    lastUpdated: "Mar 08, 2025",
  },
  {
    id: "22",
    name: "Guest",
    description: "Limited preview access for onboarding staff",
    users: 2,
    type: "custom",
    status: "inactive",
    lastUpdated: "Feb 14, 2025",
  },
]

/** @deprecated Prefer mapRolesSummaryKpi from API */
export const ROLES_KPI = mapRolesSummaryKpi({
  total_roles: 22,
  active_roles: 17,
  permissions: 96,
})
